"use client";

import {
  type HTMLAttributes,
  type MouseEvent,
  useRef,
  useState,
  type FC,
  type Ref,
} from "react";
import { mergeClasses } from "@huub-web/utils";

import { Card, type CardProps } from "../Card";
import { Text } from "../Text";
import { IconButton } from "../IconButton";
import { RadialMenu, type RadialMenuOptionData } from "../RadialMenu";
import { useHover } from "../../hooks/useHover";
import type { IconName } from "../../theme";
import { Flex } from "../Flex";

import {
  addCardStyle,
  contentStyle,
  iconButtonStyle,
  childrenStyle,
  textContainerStyle,
  clickableCardStyle,
} from "./AddCard.css";

export type AddCardOptionRecord = {
  /**
   * Unique identifier for the option
   */
  id: string;
  /**
   * Icon to display for this option
   */
  icon: IconName;
  /**
   * Label text shown on hover
   */
  label: string;
  /**
   * Short description shown below the label
   */
  subtitle?: string;
  /**
   * Callback when option is clicked
   */
  onClick?: () => void;
};

export type AddCardProps = Omit<HTMLAttributes<HTMLButtonElement>, "type"> &
  Pick<CardProps, "disabled" | "trailing" | "applyMotion"> & {
    title?: string;
    description?: string;
    options?: AddCardOptionRecord[];
    /**
     * When true, radial menu options render at full pill width with labels
     * always visible instead of collapsing to icon-only.
     */
    expandedOptions?: boolean;
    /**
     * @deprecated
     */
    icon?: IconName;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLButtonElement | HTMLDivElement>;
  };

/**
 * AddCard is a card component that can be used to display information and a single action.
 */
export const AddCard: FC<AddCardProps> = ({
  title,
  description,
  disabled = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  icon: _icon = "add",
  options,
  expandedOptions = false,
  className,
  onClick,
  children,
  ref,
  ...props
}) => {
  const radialMenuRef = useRef<HTMLDivElement>(null);
  const hasOptions = options && options.length > 0;

  const isInteractive = !!onClick && !disabled && !hasOptions;

  const { handleEnter, handleLeave, hasHover } = useHover({
    disabled: !isInteractive || disabled,
  });

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick?.(e); // TODO: Tracking
    }
  };

  const radialMenuOptions: RadialMenuOptionData[] =
    options?.map((option) => ({
      id: option.id,
      icon: option.icon,
      label: option.label,
      subtitle: option.subtitle,
      onClick: option.onClick,
    })) ?? [];

  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  /**
   * Handles card click to open RadialMenu when options exist.
   * Only opens when the menu is closed and clicking outside the RadialMenu trigger.
   */
  const handleCardClick = () => {
    if (disabled || !hasOptions || isOpen) return;
    setIsOpen(true);
  };

  return (
    <Card
      ref={ref as Ref<HTMLButtonElement>}
      as={hasOptions ? "div" : "button"}
      className={mergeClasses(
        className,
        addCardStyle({ disabled }),
        hasOptions && !disabled && !isOpen ? clickableCardStyle : undefined,
      )}
      contentClassName={contentStyle}
      onClick={
        hasOptions && !isOpen
          ? handleCardClick
          : isInteractive
            ? handleClick
            : undefined
      }
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      border="dashed"
      variant="opaque"
      disabled={disabled}
      padding="s"
      gap="xs"
      borderRadius="xs"
      {...props}
    >
      <Flex direction="column" gap="xs" align="center">
        {hasOptions ? (
          <RadialMenu
            ref={radialMenuRef}
            options={radialMenuOptions}
            disabled={disabled}
            open={isOpen}
            onOpenChange={handleOpenChange}
            expanded={expandedOptions}
            spread={90}
            startAngle={-45}
          />
        ) : (
          <IconButton
            className={iconButtonStyle}
            as="div"
            active={hasHover}
            icon="add"
            iconSize="s2"
            variant="backgroundSecondary"
            size="s"
            rounded
            border="dashed"
          />
        )}

        <Flex
          direction="column"
          gap="xxs"
          className={textContainerStyle({ isOpen })}
          grow
        >
          <Text variant="bodyS" align="center" bold={!!description}>
            {title}
          </Text>
          {description && (
            <Text variant="bodyXS" align="center" color="currentColor50">
              {description}
            </Text>
          )}
        </Flex>
      </Flex>

      {children && (
        <Flex direction="column" gap="s" className={childrenStyle}>
          {children}
        </Flex>
      )}
    </Card>
  );
};
